import { Box } from "@mui/material";
import IFCViewer from "./IFCViewer";
import { forwardRef, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import { rotateTo, zoomTo } from "./CameraControls";
import { computeMikkTSpaceTangents } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const TopBottomViewer = forwardRef(
  ({ modelURL, backgroundModels, bottomTitle, topTitle, elementIds, viewFilter, selectComponents, onSelectionChange }, ref) => {
    const { planHide, actualHide, delayedNotStart, planProgress, actualProgress, delayed, planComplete, actualComplete, showFilter, all, highlight } =
      elementIds;
    const commonProps = {
      modelURL,
      backgroundModels,
      showFilterElementIds: showFilter,
      allElementIds: all,
      highlightElementIds: highlight,
      onSelectionChange,
    };

    const topViewerRef = useRef();
    const bottomViewerRef = useRef();

    const syncCameras = (sourceViewer, targetViewer) => {
      sourceViewer.world.camera.controls.addEventListener("control", () => {
        const position = sourceViewer.world.camera.controls.getPosition(new THREE.Vector3());
        const target = sourceViewer.world.camera.controls.getTarget(new THREE.Euler());
        targetViewer.world.camera.controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z);
      });
    };

    useEffect(() => {
      if (topViewerRef.current && bottomViewerRef.current) {
        syncCameras(topViewerRef.current, bottomViewerRef.current);
        syncCameras(bottomViewerRef.current, topViewerRef.current);

        return () => {
          if (topViewerRef.current && bottomViewerRef.current) {
            topViewerRef.current.world.camera.controls.removeEventListener("control");
            bottomViewerRef.current.world.camera.controls.removeEventListener("control");
          }
        };
      }
    }, [topViewerRef, bottomViewerRef]);

    useEffect(() => {
      if (ref) {
        ref.current = {
          left: topViewerRef.current,
          right: bottomViewerRef.current,
        };
      }
      if (viewFilter === "Current view" || viewFilter === "Plan view") {
        homeViewOnClick();
      } else if (viewFilter === "Plan view") {
        rotateToViewOnClick("Top");
      }
    }, [ref, topViewerRef, bottomViewerRef, viewFilter]);

    const handleSelectChange = useCallback(
      (value, name) => {
        if (onSelectionChange) {
          onSelectionChange(value, name);
        }
      },
      [onSelectionChange]
    );

    const homeViewOnClick = () => {
      if (topViewerRef.current && bottomViewerRef.current) {
        zoomTo(topViewerRef.current.world.camera, topViewerRef.current.mainModels);
        zoomTo(bottomViewerRef.current.world.camera, bottomViewerRef.current.mainModels);
      }
    };

    const rotateToViewOnClick = (side) => {
      rotateTo(topViewerRef.current.world.camera, side);
      rotateTo(bottomViewerRef.current.world.camera, side);
    };

    return (
      <Box display="flex" flexDirection="column" width={"100%"} height={"100%"} gap={1}>
        <Box width={"100%"} height={"49.5%"}>
          <IFCViewer
            ref={bottomViewerRef}
            title={topTitle}
            hideElementIds={actualHide}
            delayedNotStartElementIds={delayedNotStart}
            inProgressElementIds={actualProgress}
            delayedElementIds={delayed}
            advanced
            {...commonProps}
          />
        </Box>
        <Box width={"100%"} height={"49.5%"}>
          <IFCViewer
            ref={topViewerRef}
            title={bottomTitle}
            hideElementIds={planHide}
            inProgressElementIds={planProgress}
            selectComponents={selectComponents}
            advanced
            {...commonProps}
          />
        </Box>
      </Box>
    );
  }
);

export default TopBottomViewer;
